import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ReportWidget from './components/ReportWidget';

const INITIAL_VISIBLE_ROWS = 6;

/**
 * Avatar component - shows user profile image or initials fallback
 */
const UserAvatar = ({ name, profileUrl }) => {
  const [imgError, setImgError] = useState(false);
  const initials = (name || '?')
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const hasRealProfile = profileUrl && !profileUrl.includes('profile.png') && !imgError;

  if (hasRealProfile) {
    return (
      <img
        src={profileUrl}
        alt={name}
        className="rounded-circle"
        style={{ width: 36, height: 36, objectFit: 'cover' }}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
      style={{
        width: 36,
        height: 36,
        backgroundColor: '#3980c0',
        fontSize: '0.75rem',
      }}
    >
      {initials}
    </div>
  );
};

/**
 * Clickable count cell - shows dash for zero, clickable number for nonzero
 */
const CountCell = ({ count, onClick }) => {
  if (count === 0) {
    return <span className="text-muted" style={{ opacity: 0.4 }}>0</span>;
  }

  return (
    <span
      className="text-primary fw-medium"
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      {count}
    </span>
  );
};

/**
 * Sortable column header with up/down chevrons
 */
const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isActive = currentSort.key === sortKey;
  const isAsc = isActive && currentSort.direction === 'asc';
  const isDesc = isActive && currentSort.direction === 'desc';

  const handleClick = () => {
    if (!isActive || isDesc) {
      onSort({ key: sortKey, direction: 'desc' });
    } else {
      onSort({ key: sortKey, direction: 'asc' });
    }
  };

  return (
    <th
      className="border-0 text-center"
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={handleClick}
    >
      <span className="d-inline-flex align-items-center gap-1">
        {label}
        <span className="d-inline-flex flex-column" style={{ lineHeight: 0 }}>
          <ChevronUp
            size={12}
            style={{ opacity: isAsc ? 1 : 0.3, marginBottom: -2 }}
          />
          <ChevronDown
            size={12}
            style={{ opacity: isDesc ? 1 : 0.3, marginTop: -2 }}
          />
        </span>
      </span>
    </th>
  );
};

const CompletedByUserWidget = ({
  data,
  loading,
  error,
  onUserWeekClick,
  onDetailsClick,
}) => {
  const { t } = useTranslation();
  const { users = [], periods = [] } = data || {};
  const isEmpty = !data || users.length === 0;

  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState({ key: null, direction: 'desc' });

  const sortedUsers = useMemo(() => {
    if (!sort.key) return users;

    return [...users].sort((a, b) => {
      let aVal, bVal;
      if (sort.key === 'totalCount') {
        aVal = a.totalCount || 0;
        bVal = b.totalCount || 0;
      } else if (sort.key.startsWith('period_')) {
        const idx = parseInt(sort.key.split('_')[1], 10);
        aVal = a.periodCounts?.[idx] || 0;
        bVal = b.periodCounts?.[idx] || 0;
      } else {
        aVal = 0;
        bVal = 0;
      }
      return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [users, sort]);

  const visibleUsers = showAll ? sortedUsers : sortedUsers.slice(0, INITIAL_VISIBLE_ROWS);

  const handleSort = useCallback((newSort) => {
    setSort(newSort);
  }, []);

  // Format period label based on duration
  const formatPeriodLabel = useCallback((period) => {
    if (!period?.start) return period?.label || '';
    const start = new Date(period.start);
    const end = period.end ? new Date(period.end) : start;
    const spanDays = (end - start) / (1000 * 60 * 60 * 24);

    if (spanDays > 20) {
      // Monthly: "Nov 2025"
      return start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    } else if (spanDays > 2) {
      // Weekly: "Nov 13"
      return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    // Daily: "Nov 13"
    return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);

  // Show at most 6 period columns to keep the table readable
  const visiblePeriods = useMemo(() => {
    const addMeta = (p, i) => ({ ...p, originalIndex: i, displayLabel: formatPeriodLabel(p) });
    if (periods.length <= 6) return periods.map(addMeta);
    // Show last 6 periods (most recent)
    return periods.slice(-6).map((p, i) => addMeta(p, periods.length - 6 + i));
  }, [periods, formatPeriodLabel]);

  return (
    <ReportWidget
      title={t('reporting.completed_by_user.title', 'Completed by User')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={Users}
      emptyMessage={t('reporting.completed_by_user.empty_message', 'No completed work orders found for the selected period.')}
    >
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th className="border-0" style={{ minWidth: '180px' }}></th>
              {visiblePeriods.map((period) => (
                <SortableHeader
                  key={period.originalIndex}
                  label={period.displayLabel}
                  sortKey={`period_${period.originalIndex}`}
                  currentSort={sort}
                  onSort={handleSort}
                />
              ))}
              <SortableHeader
                label={t('shared.total', 'Total')}
                sortKey="totalCount"
                currentSort={sort}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((user, index) => (
              <motion.tr
                key={user.userId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <UserAvatar name={user.userName} profileUrl={user.profileUrl} />
                    <span className="fw-medium text-truncate" style={{ maxWidth: '140px' }} title={user.userName}>
                      {user.userName}
                    </span>
                  </div>
                </td>
                {visiblePeriods.map((period) => (
                  <td key={period.originalIndex} className="text-center">
                    <CountCell
                      count={user.periodCounts?.[period.originalIndex] || 0}
                      onClick={() => onUserWeekClick?.(user.userId, period.originalIndex)}
                    />
                  </td>
                ))}
                <td className="text-center">
                  <span className="fw-semibold">{user.totalCount || 0}</span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* See All / Show Less */}
      {users.length > INITIAL_VISIBLE_ROWS && (
        <div className="text-end mt-2">
          <motion.button
            className="btn btn-sm btn-link text-primary text-decoration-none"
            onClick={() => setShowAll(!showAll)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {showAll ? t('shared.button_show_less', 'Show Less') : `${t('shared.button_see_all', 'See All')} (${users.length})`}
          </motion.button>
        </div>
      )}
    </ReportWidget>
  );
};

export default CompletedByUserWidget;
